"use client"
import { useState } from "react"
import { motion } from "framer-motion"
import { Link } from "react-router-dom"

const Careers = () => {
  const [activeTab, setActiveTab] = useState("all")

  const jobOpenings = [
    {
      id: 1,
      title: "AI/ML Engineer",
      department: "Research & Development",
      location: "Remote (US)",
      type: "Full-time",
      category: "technical",
      description:
        "We are looking for an AI/ML Engineer to join our R&D team to develop cutting-edge AI models for natural language processing and conversational AI.",
      responsibilities: [
        "Design and deploy scalable machine learning models and pipelines for production systems",
        "Lead end-to-end AI/ML projects, including data preprocessing, model training, and evaluation",
        "Research and implement cutting-edge techniques (e.g., transformers, reinforcement learning)",
        "Collaborate with cross-functional teams to integrate AI solutions into products",
        "Mentor junior engineers and establish best practices for code quality and reproducibility",
        "Ensure ethical AI practices, fairness, and compliance with data privacy regulations",
      ],
      requirements: [
        "Master's/PhD in Computer Science, Machine Learning, or related field (or Bachelor's with 5+ years of experience)",
        "5+ years of hands-on experience in building and deploying production-grade ML models",
        "Expertise in Python and frameworks like PyTorch/TensorFlow",
        "Proficiency with cloud platforms (AWS/GCP/Azure) and MLOps tools (MLflow, Kubeflow)",
        "Strong understanding of ML algorithms, statistics, and distributed systems",
        "Experience with NLP, computer vision, or generative AI workflows",
        "Familiarity with Docker/Kubernetes and CI/CD pipelines",
      ],
    },
    {
      id: 2,
      title: "Senior Frontend Engineer",
      department: "Engineering",
      location: "Remote (US)",
      type: "Full-time",
      category: "technical",
      description:
        "We are seeking a Senior Frontend Engineer to build beautiful, responsive, and intuitive user interfaces for our AI-powered customer service platform.",
      responsibilities: [
        "Design and implement user interfaces for our web applications",
        "Work closely with designers and product managers to translate wireframes into code",
        "Optimize applications for maximum speed and scalability",
        "Collaborate with backend engineers to integrate frontend with API services",
        "Implement responsive design and ensure cross-browser compatibility",
      ],
      requirements: [
        "Bachelor's degree in Computer Science or related field",
        "5+ years of experience in frontend development",
        "Proficiency in React.js, JavaScript, HTML5, and CSS3",
        "Experience with modern frontend tools and workflows (Webpack, ESLint, etc.)",
        "Strong understanding of UI/UX design principles",
      ],
    },
    {
      id: 3,
      title: "Machine Learning Engineer",
      department: "Engineering",
      location: "Remote (US)",
      type: "Full-time",
      category: "technical",
      description:
        "Join our engineering team to build and deploy machine learning models that power our AI customer service solutions.",
      responsibilities: [
        "Design, develop, and deploy machine learning models to production",
        "Collaborate with data scientists and researchers to implement algorithms",
        "Optimize models for performance, scalability, and reliability",
        "Develop data pipelines for model training and evaluation",
        "Monitor and maintain machine learning systems in production",
      ],
      requirements: [
        "Master's degree in Computer Science, Machine Learning, or related field",
        "3+ years of experience in machine learning engineering",
        "Strong programming skills in Python and familiarity with ML frameworks",
        "Experience with cloud platforms (AWS, GCP, or Azure)",
        "Knowledge of software engineering best practices",
      ],
    },
    {
      id: 4,
      title: "Product Manager",
      department: "Product",
      location: "Remote (US)",
      type: "Full-time",
      category: "business",
      description:
        "We are looking for a Product Manager to lead the development of our AI-powered customer service products, from conception to launch.",
      responsibilities: [
        "Define product vision, strategy, and roadmap for AI products",
        "Gather and prioritize product requirements from stakeholders",
        "Work closely with engineering, design, and marketing teams",
        "Analyze market trends and competitive landscape",
        "Define success metrics and track product performance",
      ],
      requirements: [
        "Bachelor's degree in Business, Computer Science, or related field",
        "4+ years of experience in product management",
        "Experience with AI or SaaS products",
        "Strong analytical and problem-solving skills",
        "Excellent communication and leadership abilities",
      ],
    },
    {
      id: 5,
      title: "Customer Success Manager",
      department: "Customer Success",
      location: "Remote (US)",
      type: "Full-time",
      category: "business",
      description:
        "Join our Customer Success team to ensure our clients achieve maximum value from our AI solutions and become long-term advocates for AlfasTech.",
      responsibilities: [
        "Serve as the primary point of contact for assigned clients",
        "Develop and maintain strong relationships with key stakeholders",
        "Ensure successful onboarding and implementation of our solutions",
        "Identify opportunities for expansion within existing accounts",
        "Work with product and engineering teams to address client needs",
      ],
      requirements: [
        "Bachelor's degree in Business or related field",
        "3+ years of experience in customer success or account management",
        "Strong interpersonal and communication skills",
        "Experience with CRM systems and customer success tools",
        "Ability to understand technical concepts and explain them to non-technical audiences",
      ],
    },
    {
      id: 6,
      title: "Data Security Engineer",
      department: "Engineering",
      location: "Remote (US)",
      type: "Full-time",
      category: "technical",
      description:
        "We are seeking a Data Security Engineer to design and implement robust security frameworks for our data infrastructure, ensuring compliance and protection of sensitive information.",
      responsibilities: [
        "Design and maintain secure data pipelines and storage systems with encryption and access controls",
        "Implement security protocols for data ingestion, transformation, and storage",
        "Develop auditing and monitoring systems to detect and mitigate security threats",
        "Collaborate with IT and compliance teams to ensure adherence to GDPR, HIPAA, and other regulations",
        "Conduct vulnerability assessments and penetration testing on data systems",
        "Respond to data breaches or incidents and implement remediation strategies",
      ],
      requirements: [
        "Bachelor's degree in Cybersecurity, Computer Science, or related field (Master's preferred)",
        "3+ years of experience in data security, cybersecurity, or infrastructure protection",
        "Proficiency in security frameworks (e.g., NIST, ISO 27001) and encryption methods (AES, TLS)",
        "Experience with cloud security tools (AWS KMS, Azure Key Vault, GCP IAM)",
        "Knowledge of data governance, compliance, and privacy laws (GDPR, CCPA)",
        "Strong scripting skills in Python or Bash for security automation",
        "Familiarity with SIEM tools (Splunk, Datadog) and threat detection systems",
      ],
    },
  ]

  const filteredJobs = activeTab === "all" ? jobOpenings : jobOpenings.filter((job) => job.category === activeTab)

  const benefits = [
    {
      title: "Comprehensive Healthcare",
      description: "Full medical, dental, and vision coverage for employees and dependents",
      icon: (
        <svg
          className="w-12 h-12 text-white"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
          />
        </svg>
      ),
    },
    {
      title: "Flexible Work",
      description: "Remote work options and flexible scheduling to support work-life balance",
      icon: (
        <svg
          className="w-12 h-12 text-white"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
          />
        </svg>
      ),
    },
    {
      title: "Competitive Compensation",
      description: "Competitive salary, equity options, and performance bonuses",
      icon: (
        <svg
          className="w-12 h-12 text-white"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
      ),
    },
    {
      title: "Professional Development",
      description: "Continuous learning opportunities, conference attendance, and education stipends",
      icon: (
        <svg
          className="w-12 h-12 text-white"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
          />
        </svg>
      ),
    },
    {
      title: "Generous Time Off",
      description: "Unlimited PTO policy, paid holidays, and parental leave",
      icon: (
        <svg
          className="w-12 h-12 text-white"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
          />
        </svg>
      ),
    },
    {
      title: "Wellness Programs",
      description: "Gym memberships, wellness stipends, and mental health resources",
      icon: (
        <svg
          className="w-12 h-12 text-white"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
        </svg>
      ),
    },
  ]

  return (
    <div className="flex flex-col min-h-screen bg-background">
      {/* Hero Section */}
      <section className="pt-32 pb-20 relative overflow-hidden">
        {/* Background Elements */}
        <div className="absolute inset-0 z-0 overflow-hidden">
          <div className="absolute top-0 left-0 w-full h-full bg-background">
            {/* Grid pattern */}
            <div className="absolute inset-0 opacity-20">
              <div
                className="absolute inset-0"
                style={{
                  backgroundImage:
                    "linear-gradient(#5F72FF22 1px, transparent 1px), linear-gradient(90deg, #5F72FF22 1px, transparent 1px)",
                  backgroundSize: "40px 40px",
                }}
              ></div>
            </div>

            {/* Animated gradient blobs */}
            <motion.div
              initial={{ scale: 0.8, opacity: 0.3 }}
              animate={{
                scale: [0.8, 1.2, 0.9, 1.1, 0.8],
                opacity: [0.3, 0.4, 0.2, 0.5, 0.3],
                x: ["-10%", "5%", "0%", "-5%", "-10%"],
                y: ["5%", "-5%", "10%", "0%", "5%"],
              }}
              transition={{ duration: 20, repeat: Number.POSITIVE_INFINITY, repeatType: "loop" }}
              className="absolute top-1/4 left-1/4 w-[40vw] h-[40vw] rounded-full bg-accent opacity-20 blur-[100px]"
            />
          </div>
        </div>

        <div className="container-custom relative z-10">
          <div className="text-center max-w-3xl mx-auto">
            <motion.h1
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-4xl md:text-5xl font-bold text-white mb-6 font-futuristic"
            >
              Join Our Team
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-xl text-white mb-8"
            >
              Help us build the future of AI-powered customer service. We're looking for passionate, innovative
              individuals to join our growing team.
            </motion.p>
          </div>
        </div>
      </section>

      {/* Why Join Us Section */}
      <section className="py-20 bg-background">
        <div className="container-custom">
          <div className="text-center mb-16">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-3xl md:text-4xl font-bold text-white mb-4 font-futuristic"
            >
              Why Join AlfasTech?
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-xl text-white max-w-3xl mx-auto"
            >
              We're building something special, and we want you to be part of it
            </motion.p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                title: "Cutting-Edge Technology",
                description:
                  "Work with the latest AI technologies and contribute to advancing the field of artificial intelligence.",
                icon: (
                  <svg
                    className="w-12 h-12 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
                    />
                  </svg>
                ),
              },
              {
                title: "Meaningful Impact",
                description:
                  "Our solutions help businesses transform their operations and deliver exceptional customer experiences.",
                icon: (
                  <svg
                    className="w-12 h-12 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M14 10h4.764a2 2 0 011.789 2.894l-3.5 7A2 2 0 0115.263 21h-4.017c-.163 0-.326-.02-.485-.06L7 20m7-10V5a2 2 0 00-2-2h-.095c-.5 0-.905.405-.905.905 0 .714-.211 1.412-.608 2.006L7 11v9m7-10h-2M7 20H5a2 2 0 01-2-2v-6a2 2 0 012-2h2.5"
                    />
                  </svg>
                ),
              },
              {
                title: "Growth Opportunities",
                description:
                  "As a fast-growing company, we offer abundant opportunities for professional development and career advancement.",
                icon: (
                  <svg
                    className="w-12 h-12 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"
                    />
                  </svg>
                ),
              },
              {
                title: "Collaborative Culture",
                description:
                  "We foster a collaborative, inclusive environment where diverse perspectives are valued and everyone can contribute.",
                icon: (
                  <svg
                    className="w-12 h-12 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                    />
                  </svg>
                ),
              },
              {
                title: "Work-Life Balance",
                description:
                  "We believe in sustainable work practices and support our team members in maintaining a healthy work-life balance.",
                icon: (
                  <svg
                    className="w-12 h-12 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                ),
              },
              {
                title: "Innovation-Driven",
                description: "We encourage creative thinking and empower our team to explore new ideas and approaches.",
                icon: (
                  <svg
                    className="w-12 h-12 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4"
                    />
                  </svg>
                ),
              },
            ].map((item, index) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="backdrop-blur-xl bg-white/10 p-8 rounded-2xl border-2 border-white/20 hover:border-accent/70 transition-all duration-300 group shadow-xl"
              >
                <div className="mb-6 text-white group-hover:text-highlight transition-colors duration-300">
                  <div className="w-16 h-16 flex items-center justify-center rounded-xl bg-gradient-to-br from-accent to-accent2 border-2 border-white/50 group-hover:border-highlight/50 transition-all duration-300 shadow-lg shadow-accent/30">
                    {item.icon}
                  </div>
                </div>
                <h3 className="text-xl font-bold mb-2 text-white group-hover:text-highlight transition-colors duration-300">
                  {item.title}
                </h3>
                <p className="text-white group-hover:text-white transition-colors duration-300">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-background/50">
        <div className="container-custom">
          <div className="text-center mb-16">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-3xl md:text-4xl font-bold text-white mb-4 font-futuristic"
            >
              Benefits & Perks
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-xl text-white max-w-3xl mx-auto"
            >
              We take care of our team so they can focus on doing their best work
            </motion.p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => (
              <motion.div
                key={benefit.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="backdrop-blur-xl bg-white/10 p-8 rounded-2xl border-2 border-white/20 hover:border-accent/70 transition-all duration-300 group shadow-xl"
              >
                <div className="mb-6 text-white group-hover:text-highlight transition-colors duration-300">
                  <div className="w-16 h-16 flex items-center justify-center rounded-xl bg-gradient-to-br from-accent to-accent2 border-2 border-white/50 group-hover:border-highlight/50 transition-all duration-300 shadow-lg shadow-accent/30">
                    {benefit.icon}
                  </div>
                </div>
                <h3 className="text-xl font-bold mb-2 text-white group-hover:text-highlight transition-colors duration-300">
                  {benefit.title}
                </h3>
                <p className="text-white group-hover:text-white transition-colors duration-300">
                  {benefit.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Open Positions Section */}
      <section className="py-20 bg-background">
        <div className="container-custom">
          <div className="text-center mb-16">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-3xl md:text-4xl font-bold text-white mb-4 font-futuristic"
            >
              Open Positions
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-xl text-white max-w-3xl mx-auto"
            >
              Find your next opportunity at AlfasTech
            </motion.p>
          </div>

          <div className="mb-8">
            <div className="flex flex-wrap justify-center gap-4">
              <button
                onClick={() => setActiveTab("all")}
                className={`px-6 py-2 rounded-full transition-colors ${
                  activeTab === "all"
                    ? "bg-gradient-to-r from-accent to-accent2 text-white"
                    : "bg-white/10 text-white hover:bg-white/20"
                }`}
              >
                All Positions
              </button>
              <button
                onClick={() => setActiveTab("technical")}
                className={`px-6 py-2 rounded-full transition-colors ${
                  activeTab === "technical"
                    ? "bg-gradient-to-r from-accent to-accent2 text-white"
                    : "bg-white/10 text-white hover:bg-white/20"
                }`}
              >
                Technical
              </button>
              <button
                onClick={() => setActiveTab("business")}
                className={`px-6 py-2 rounded-full transition-colors ${
                  activeTab === "business"
                    ? "bg-gradient-to-r from-accent to-accent2 text-white"
                    : "bg-white/10 text-white hover:bg-white/20"
                }`}
              >
                Business
              </button>
            </div>
          </div>

          <div className="space-y-6">
            {filteredJobs.map((job) => (
              <motion.div
                key={job.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4 }}
                className="backdrop-blur-xl bg-white/10 rounded-2xl overflow-hidden border-2 border-white/20 hover:border-accent/70 transition-all duration-300 group shadow-xl"
              >
                <div className="p-6">
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-4">
                    <div>
                      <h3 className="text-xl font-bold text-white group-hover:text-highlight transition-colors duration-300">
                        {job.title}
                      </h3>
                      <p className="text-accent">{job.department}</p>
                    </div>
                    <div className="mt-2 md:mt-0 flex flex-wrap gap-2">
                      <span className="inline-block px-3 py-1 text-sm bg-white/10 rounded-full text-white">
                        {job.location}
                      </span>
                      <span className="inline-block px-3 py-1 text-sm bg-white/10 rounded-full text-white">
                        {job.type}
                      </span>
                    </div>
                  </div>
                  <p className="text-white mb-6">{job.description}</p>

                  <div className="mb-6">
                    <h4 className="font-bold text-white mb-2">Responsibilities:</h4>
                    <ul className="list-disc list-inside space-y-1 text-white">
                      {job.responsibilities.map((responsibility, index) => (
                        <li key={index}>{responsibility}</li>
                      ))}
                    </ul>
                  </div>

                  <div className="mb-6">
                    <h4 className="font-bold text-white mb-2">Requirements:</h4>
                    <ul className="list-disc list-inside space-y-1 text-white">
                      {job.requirements.map((requirement, index) => (
                        <li key={index}>{requirement}</li>
                      ))}
                    </ul>
                  </div>

                  <div className="flex justify-end">
                    <Link
                      to={`/careers/${job.id}`}
                      className="px-6 py-3 rounded-full font-bold text-white bg-gradient-to-r from-accent to-accent2 shadow-lg hover:shadow-accent/50 hover:scale-105 transition-all duration-300 border-2 border-white/20"
                    >
                      Apply Now
                    </Link>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-accent/30 to-accent2/30 relative overflow-hidden">
        {/* Animated Orbs */}
        <div className="absolute top-1/4 left-1/4 w-64 h-64 rounded-full bg-accent opacity-20 blur-3xl animate-pulse-slow"></div>
        <div className="absolute bottom-1/4 right-1/4 w-64 h-64 rounded-full bg-accent2 opacity-20 blur-3xl animate-pulse-slower"></div>

        <div className="container-custom relative z-10">
          <div className="backdrop-blur-lg bg-background/80 border border-white/20 rounded-3xl p-12 md:p-16 max-w-4xl mx-auto">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-3xl md:text-4xl font-bold mb-6 text-white text-center font-futuristic"
            >
              Don't See a Perfect Fit?
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-xl mb-8 text-white text-center"
            >
              We're always looking for talented individuals who are passionate about AI and innovation. Send us your
              resume and let us know how you can contribute to our mission.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="flex justify-center"
            >
              <Link
                to="/contact"
                className="px-8 py-4 rounded-full font-bold text-lg bg-gradient-to-r from-accent to-accent2 shadow-xl hover:scale-105 hover:shadow-2xl transition-all duration-300 focus:outline-none focus:ring-4 focus:ring-accent2/40 text-white border-2 border-white/20"
              >
                Contact Us
              </Link>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  )
}

export default Careers
